<template>
  <v-container>
    <v-row v-if="loadingRecords" justify="center" align="center">
      <v-progress-circular indeterminate :size="60" color="primary" />
    </v-row>

    <v-row v-else-if="APIError" justify="center" align="center">
      <p>
        <v-icon color="error">mdi-alert-circle-outline</v-icon> Sorry, something
        went wrong. Please try again later.
      </p>
    </v-row>

    <div v-else>
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>
              <v-row align="center" no-gutters>
                <v-btn icon @click="changeWeek(-7)">
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                <span class="mx-2">Week of {{ currentWeekDates[0] }}</span>
                <v-btn icon @click="changeWeek(7)">
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </v-row>
            </v-card-title>
            <apexchart height="350" :options="weeklyChartOptions" :series="weeklyChartSeries"></apexchart>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <PomodoroReviewCard v-for="category in Object.keys(pomodoroCategories)" :key="category"
          :title="pomodoroCategories[category].label" :value="weeklyPomodorosByCategory[category]"
          :goal="pomodoroCategories[category].targetWeeklyGoal" :isTracked="pomodoroCategories[category].isTracked">
        </PomodoroReviewCard>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title>
              <p class="headline">Pomodoro Sessions</p>
            </v-card-title>
            <v-data-table :headers="tableHeaders" :items="$store.state.pomodoroSessions">
              <template v-slot:[`item.category`]="{ item }">
                <v-chip :color="pomodoroCategories[item.category].color" text-color="white" small>
                  {{ pomodoroCategories[item.category].label }}
                </v-chip>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-icon @click.stop="deletePomodoro(item)"> mdi-delete </v-icon>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <v-btn bottom color="primary" dark fab fixed right @click="createNewPomodoro">
      <v-icon>mdi-plus</v-icon>
    </v-btn>

    <v-dialog v-model="dialog" width="800px">
      <PomodoroForm :key="dialogKey" :propsPomodoro="selectedPomodoro" :action="dialogAction"
        @close-dialog="closeDialog" @createdPomodoro="getData">
      </PomodoroForm>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";
import VueApexCharts from 'vue-apexcharts'
import PomodoroForm from "./PomodoroForm";
import PomodoroReviewCard from "./PomodoroReviewCard";

export default {
  name: "PomodoroReview",

  components: {
    apexchart: VueApexCharts,
    PomodoroForm,
    PomodoroReviewCard,
  },

  data: () => ({
    APIError: false,
    loadingRecords: false,
    dialog: false,
    dialogAction: "create",
    dialogKey: "",
    selectedPomodoro: undefined,
    selectedDate: new Date(),
    stats: [
      { title: "Total Pomodoros", value: 0 },
      { title: "Total Time", value: 0 },
      { title: "Average Time", value: 0 },
      { title: "Longest Session", value: 0 },
      { title: "Shortest Session", value: 0 },
    ],
    pomodoroCategories: {
      work: { label: "Work", color: "light-blue", chartColor: "#03A9F4", targetWeeklyGoal: 56, isTracked: true },
      task: { label: "Task", color: "purple lighten-1", chartColor: "#CE93D8", targetWeeklyGoal: 0, isTracked: false },
      health: { label: "Health", color: "green", chartColor: "#4CAF50", targetWeeklyGoal: 4, isTracked: true },
      learning: { label: "Learning", color: "blue-grey", chartColor: "#607D8B", targetWeeklyGoal: 7, isTracked: true },
      reading: { label: "Reading", color: "cyan", chartColor: "#00BCD4", targetWeeklyGoal: 7, isTracked: true },
      finances: { label: "Finances", color: "red", chartColor: "#F44336", targetWeeklyGoal: 0, isTracked: false },
      other: { label: "Other", color: "orange", chartColor: "#FF9800", targetWeeklyGoal: 0, isTracked: false },
    },
    tableHeaders: [
      { text: "Task", value: "task" },
      { text: "Category", value: "category" },
      { text: "Date", value: "date" },
      { text: "", value: "actions" },
    ],
  }),

  mounted() {
    if (this.pomodoroSessions.length == 0) {
      this.getData();
    }
  },

  computed: {
    ...mapState(["pomodoroSessions"]),
    currentWeekDates() {
      const date = new Date(this.selectedDate);
      const day = date.getDay();
      // getDay() returns 0 for Sunday, so we need to handle that case specially
      const diff = date.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(date.setDate(diff));

      return Array.from({ length: 7 }, (_, i) => {
        const date = new Date(monday);
        date.setDate(monday.getDate() + i);
        // Converting to Canadian locale instead of ISO to avoid timezone issues
        return date.toLocaleDateString('en-CA');
      });
    },
    chartCategories() {
      return this.currentWeekDates;
    },
    weeklyChartSeries() {
      const categoryData = {};
      Object.keys(this.pomodoroCategories).forEach(category => {
        categoryData[category] = Array(7).fill(0);
      });
      this.pomodoroSessions.forEach(session => {
        const dateIndex = this.currentWeekDates.indexOf(session.date);
        if (dateIndex !== -1) {
          categoryData[session.category][dateIndex]++;
        }
      });
      return Object.keys(this.pomodoroCategories).map(category => ({
        name: this.pomodoroCategories[category].label,
        data: categoryData[category]
      }));
    },
    weeklyPomodorosByCategory() {
      const categoryCounts = {};
      Object.keys(this.pomodoroCategories).forEach(category => {
        categoryCounts[category] = 0;
      });
      this.pomodoroSessions.forEach(session => {
        if (this.currentWeekDates.includes(session.date)) {
          categoryCounts[session.category]++;
        }
      });
      return categoryCounts;
    },
    dailyTotals() {
      const totals = Array(7).fill(0);
      this.weeklyChartSeries.forEach(series => {
        series.data.forEach((value, dayIndex) => {
          totals[dayIndex] += value;
        });
      });
      return totals;
    },
    maxStackedValue() {
      return Math.max(...this.dailyTotals);
    },
    weeklyChartOptions() {
      return {
        chart: {
          type: 'bar',
          stacked: true,
          fontFamily: 'Roboto, sans-serif',
          toolbar: {
            show: false
          }
        },
        plotOptions: {
          bar: {
            horizontal: false,
          },
        },
        xaxis: {
          categories: this.chartCategories,
        },
        yaxis: {
          min: 0,
          max: Math.max(12, this.maxStackedValue),
          tickAmount: 6,
        },
        legend: {
          position: 'top',
        },
        dataLabels: {
          enabled: true,
        },
        colors: Object.values(this.pomodoroCategories).map(cat => cat.chartColor),
        annotations: {
          yaxis: [{
            y: 10,
            borderColor: '#28b5f5',
            label: {
              borderColor: '#28b5f5',
              style: {
                color: '#fff',
                background: '#28b5f5'
              },
            }
          }]
        }
      }
    },
  },

  methods: {
    getData: function () {
      this.loadingRecords = true;
      this.APIError = false;
      axios
        .get(
          `${process.env.VUE_APP_API_ROOT_URL}pomodoro/sessions/`,
          this.$store.getters.axiosConfig
        )
        .then((response) => {
          this.$store.commit("updatePomodoroSessions", response.data);
          this.loadingRecords = false;
        })
        .catch(() => {
          this.APIError = true;
          this.loadingRecords = false;
        });
    },
    createNewPomodoro: function () {
      this.selectedPomodoro = undefined;
      this.dialogAction = "create";
      this.openDialog();
    },
    editPomodoro: function (pomodoroSession) {
      this.selectedPomodoro = pomodoroSession;
      this.dialogAction = "edit";
      this.openDialog();
    },
    deletePomodoro: function (pomodoroSession) {
      this.selectedPomodoro = pomodoroSession;
      this.dialogAction = "delete";
      this.openDialog();
    },
    openDialog: function () {
      this.dialogKey = this.selectedPomodoro
        ? this.selectedPomodoro.id
        : "new";
      this.dialog = true;
    },
    closeDialog: function () {
      this.dialogKey = "";
      this.dialog = false;
    },
    changeWeek(days) {
      const newDate = new Date(this.selectedDate.getTime());
      newDate.setDate(newDate.getDate() + days);
      this.selectedDate = newDate;
    },
  },
};
</script>
